import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  db,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "../firebase";
import {
  storage,
  storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "../firebase";
import LoadingSpinner from "../components/LoadingSpinner";
import CollapsibleSection from "../components/CollapsibleSection";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ document, onClose, onEdit, onDelete }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for Document: {document.doc_type || document.custom_doc_name || "Unknown"}</h3>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onEdit}>
            Edit
          </button>
          <button className="btn-delete" onClick={onDelete}>
            Delete
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterModal = ({ isOpen, onClose, filters, setFilters, trucks, onApply, onReset }) => {
  const modalRef = useRef(null);
  const [tempFilters, setTempFilters] = useState(filters);

  useEffect(() => {
    if (isOpen) {
      setTempFilters(filters);
    }
  }, [isOpen, filters]);

  const handleApply = () => {
    setFilters(tempFilters);
    onApply();
    onClose();
  };

  const handleReset = () => {
    const reset = {
      truck_reg: "",
      doc_type: "",
      expiry_from: "",
      expiry_to: "",
      status: "",
    };
    setTempFilters(reset);
    setFilters(reset);
    onReset();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "650px" }}>
        <h3>Filter Documents</h3>
        <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div className="form-cell">
            <label>Truck Reg No</label>
            <select
              value={tempFilters.truck_reg}
              onChange={(e) => setTempFilters(prev => ({ ...prev, truck_reg: e.target.value }))}
            >
              <option value="">All Trucks</option>
              {trucks.map((t) => (
                <option key={t.id} value={t.reg_no}>
                  {t.reg_no}
                </option>
              ))}
            </select>
          </div>

          <div className="form-cell">
            <label>Document Type</label>
            <select
              value={tempFilters.doc_type}
              onChange={(e) => setTempFilters(prev => ({ ...prev, doc_type: e.target.value }))}
            >
              <option value="">All Types</option>
              <option>Disc</option>
              <option>Cross Border Permit</option>
              <option>Operator Card</option>
              <option>RIB</option>
              <option>Carriers License</option>
              <option>Insurance</option>
              <option>Roadworthy Certificate</option>
              <option>Other</option>
            </select>
          </div>

          <div className="form-cell">
            <label>Expiry From</label>
            <input
              type="date"
              value={tempFilters.expiry_from}
              onChange={(e) => setTempFilters(prev => ({ ...prev, expiry_from: e.target.value }))}
            />
          </div>

          <div className="form-cell">
            <label>Expiry To</label>
            <input
              type="date"
              value={tempFilters.expiry_to}
              onChange={(e) => setTempFilters(prev => ({ ...prev, expiry_to: e.target.value }))}
            />
          </div>

          <div className="form-cell">
            <label>Status</label>
            <select
              value={tempFilters.status}
              onChange={(e) => setTempFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="valid">Valid</option>
              <option value="expiring">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: "24px" }}>
          <button className="btn-primary" onClick={handleApply}>
            Apply Filters
          </button>
          <button className="btn-secondary" onClick={handleReset}>
            Reset All
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Documents() {
  const location = useLocation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [actionDocId, setActionDocId] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    summaryCards: true,
    documentForm: true,
    documentsList: true,
  });

  const [form, setForm] = useState({
    doc_type: "",
    custom_doc_name: "",
    owner_type: "truck",
    owner_id: "",
    issue_date: "",
    expiry_date: "",
    is_shared: false,
    notes: "",
    file: null,
    file_name: "",
    file_url: "",
  });

  const [filters, setFilters] = useState({
    truck_reg: "",
    doc_type: "",
    expiry_from: "",
    expiry_to: "",
    status: "",
  });

  const filterTruck = location.state?.filterTruck || "";

  useEffect(() => {
    if (filterTruck) {
      setFilters(prev => ({ ...prev, truck_reg: filterTruck }));
      setForm(prev => ({ ...prev, owner_type: "truck", owner_id: filterTruck }));
    }
  }, [filterTruck]);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      try {
        await Promise.all([loadDocuments(), loadTrucks()]);
      } catch (error) {
        console.error("Error loading data:", error);
        setFeedback({ message: "Failed to load documents.", type: "error" });
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  async function loadDocuments() {
    try {
      const snaps = await getDocs(collection(db, "documents"));
      const docsData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocuments(docsData);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  }

  async function loadTrucks() {
    try {
      const snaps = await getDocs(collection(db, "trucks"));
      const trucksData = snaps.docs
        .map((d) => ({ id: d.id, reg_no: d.data().reg_no }))
        .filter((t) => t.reg_no);
      setTrucks(trucksData);
    } catch (error) {
      console.error("Error fetching trucks:", error);
      setTrucks([]);
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({
        ...prev,
        file,
        file_name: file.name,
      }));
    }
  };

  const handleDeleteFile = () => {
    if (!window.confirm("Remove this file? You can upload a new one.")) return;
    setForm(prev => ({
      ...prev,
      file: null,
      file_name: "",
      file_url: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const finalDocType = form.doc_type === "Other" ? form.custom_doc_name.trim() : form.doc_type;
    if (!finalDocType || !form.owner_type || !form.owner_id || !form.issue_date || !form.expiry_date) {
      setFeedback({ message: "Please fill in all required fields.", type: "error" });
      return;
    }

    try {
      let file_url = form.file_url;

      if (form.file) {
        const fileRef = storageRef(storage, `documents/${Date.now()}_${form.file.name}`);
        await uploadBytes(fileRef, form.file);
        file_url = await getDownloadURL(fileRef);
      }

      const payload = {
        doc_type: form.doc_type,
        custom_doc_name: form.doc_type === "Other" ? finalDocType : "",
        owner_type: form.owner_type,
        owner_id: form.owner_id,
        issue_date: form.issue_date,
        expiry_date: form.expiry_date,
        is_shared: form.is_shared,
        notes: form.notes || "",
        file_name: form.file ? form.file.name : form.file_name,
        file_url,
        updatedAt: serverTimestamp(),
      };

      if (editingDocId) {
        if (form.file && form.file_url && form.file_url !== file_url) {
          try {
            const oldRef = storageRef(storage, form.file_url);
            await deleteObject(oldRef);
          } catch (err) {
            console.warn("Could not delete old file:", err);
          }
        }
        await updateDoc(doc(db, "documents", editingDocId), payload);
        setFeedback({ message: "Document updated successfully.", type: "success" });
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, "documents"), payload);
        setFeedback({ message: "Document added successfully.", type: "success" });
      }

      resetForm();
      await loadDocuments();
    } catch (error) {
      console.error("Error saving document:", error);
      setFeedback({ message: "Failed to save document.", type: "error" });
    }
  };

  const resetForm = () => {
    setForm({
      doc_type: "",
      custom_doc_name: "",
      owner_type: "truck",
      owner_id: filterTruck || "",
      issue_date: "",
      expiry_date: "",
      is_shared: false,
      notes: "",
      file: null,
      file_name: "",
      file_url: "",
    });
    setEditingDocId(null);
  };

  const openEdit = (doc) => {
    setForm({
      doc_type: doc.custom_doc_name ? "Other" : doc.doc_type || "",
      custom_doc_name: doc.custom_doc_name || "",
      owner_type: doc.owner_type || "truck",
      owner_id: doc.owner_id || "",
      issue_date: doc.issue_date || "",
      expiry_date: doc.expiry_date || "",
      is_shared: doc.is_shared || false,
      notes: doc.notes || "",
      file: null,
      file_name: doc.file_name || "",
      file_url: doc.file_url || "",
    });
    setEditingDocId(doc.id);
    setActionDocId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document permanently?")) return;

    try {
      const docToDelete = documents.find(d => d.id === id);
      if (docToDelete?.file_url) {
        try {
          const fileRef = storageRef(storage, docToDelete.file_url);
          await deleteObject(fileRef);
        } catch (err) {
          console.warn("Could not delete file:", err);
        }
      }
      await deleteDoc(doc(db, "documents", id));
      setFeedback({ message: "Document deleted.", type: "success" });
      await loadDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      setFeedback({ message: "Failed to delete document.", type: "error" });
    }
  };

  const getStatus = (expiryDate) => {
    if (!expiryDate) return { text: "No Expiry", color: "#6b7280" };
    const exp = new Date(expiryDate);
    const today = new Date();
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: "Expired", color: "#b91c1c" };
    if (diffDays <= 14) return { text: "Expiring Soon", color: "#d97706" };
    return { text: "Valid", color: "#15803d" };
  };

  const filteredDocuments = documents.filter((doc) => {
    if (filters.truck_reg && (doc.owner_type !== "truck" || doc.owner_id !== filters.truck_reg)) return false;
    if (filters.doc_type) {
      const docName = doc.custom_doc_name || doc.doc_type;
      if (docName !== filters.doc_type) return false;
    }
    if (filters.expiry_from && doc.expiry_date < filters.expiry_from) return false;
    if (filters.expiry_to && doc.expiry_date > filters.expiry_to) return false;
    if (filters.status) {
      const status = getStatus(doc.expiry_date);
      if (filters.status === "valid" && status.text !== "Valid") return false;
      if (filters.status === "expiring" && status.text !== "Expiring Soon") return false;
      if (filters.status === "expired" && status.text !== "Expired") return false;
    }
    return true;
  });

  const metrics = {
    total: filteredDocuments.length,
    valid: filteredDocuments.filter(d => getStatus(d.expiry_date).text === "Valid").length,
    expiring: filteredDocuments.filter(d => getStatus(d.expiry_date).text === "Expiring Soon").length,
    expired: filteredDocuments.filter(d => getStatus(d.expiry_date).text === "Expired").length,
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const resetFilters = () => {
    setFilters({
      truck_reg: "",
      doc_type: "",
      expiry_from: "",
      expiry_to: "",
      status: "",
    });
    if (filterTruck) {
      navigate("/documents", { replace: true });
    }
  };

  return (
    <div className="documents-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {feedback && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}

          <h2>Compliance Documents</h2>

          {filterTruck && (
            <div style={{ marginBottom: "16px", padding: "10px", background: "#e0f2fe", borderRadius: "6px" }}>
              <strong>Filtering documents for truck: {filterTruck}</strong>
              <button
                className="btn-secondary"
                style={{ marginLeft: "12px", fontSize: "12px" }}
                onClick={resetFilters}
              >
                Clear Filter
              </button>
            </div>
          )}

          <CollapsibleSection
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Summary</span>
                <button
                  className="btn-secondary"
                  style={{ fontSize: "13px", padding: "6px 12px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterModal(true);
                  }}
                >
                  Filter Documents
                </button>
              </div>
            }
            id="summaryCards"
            isExpanded={expandedSections.summaryCards}
            onToggle={() => handleSectionToggle("summaryCards")}
            externalControl={true}
          >
            <div className="summary-cards">
              <div className="card">
                <div className="card-title">Total Documents</div>
                <div className="card-value">{metrics.total}</div>
              </div>
              <div className="card">
                <div className="card-title">Valid</div>
                <div className="card-value" style={{ color: "#15803d" }}>
                  {metrics.valid}
                </div>
              </div>
              <div className="card">
                <div className="card-title">Expiring Soon (≤14 days)</div>
                <div className="card-value" style={{ color: "#d97706", fontWeight: "bold" }}>
                  {metrics.expiring}
                </div>
              </div>
              <div className="card">
                <div className="card-title">Expired</div>
                <div className="card-value" style={{ color: "#b91c1c", fontWeight: "bold" }}>
                  {metrics.expired}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={editingDocId ? "Edit Document" : "Add Document"}
            id="documentForm"
            isExpanded={expandedSections.documentForm}
            onToggle={() => handleSectionToggle("documentForm")}
            externalControl={true}
          >
            <form onSubmit={handleSubmit} className="form-grid">
              <div className="form-cell">
                <label>Document Type <span style={{ color: "red" }}>*</span></label>
                <select
                  value={form.doc_type}
                  onChange={(e) => setForm({ ...form, doc_type: e.target.value, custom_doc_name: "" })}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Disc">Disc</option>
                  <option value="Cross Border Permit">Cross Border Permit</option>
                  <option value="Operator Card">Operator Card</option>
                  <option value="RIB">RIB (Remover in Bond)</option>
                  <option value="Carriers License">Carriers License</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Roadworthy Certificate">Roadworthy Certificate</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {form.doc_type === "Other" && (
                <div className="form-cell">
                  <label>Custom Document Name <span style={{ color: "red" }}>*</span></label>
                  <input
                    type="text"
                    value={form.custom_doc_name}
                    onChange={(e) => setForm({ ...form, custom_doc_name: e.target.value })}
                    placeholder="Enter document name"
                    required
                  />
                </div>
              )}

              <div className="form-cell">
                <label>Owner Type <span style={{ color: "red" }}>*</span></label>
                <select
                  value={form.owner_type}
                  onChange={(e) => setForm({ ...form, owner_type: e.target.value, owner_id: "" })}
                  required
                >
                  <option value="truck">Truck</option>
                  <option value="company">Company (Shared)</option>
                </select>
              </div>

              {form.owner_type === "truck" && (
                <div className="form-cell">
                  <label>Truck Reg No <span style={{ color: "red" }}>*</span></label>
                  <select
                    value={form.owner_id}
                    onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                    required
                  >
                    <option value="">Select Truck</option>
                    {trucks.map((t) => (
                      <option key={t.id} value={t.reg_no}>
                        {t.reg_no}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-cell">
                <label>Issue Date <span style={{ color: "red" }}>*</span></label>
                <input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-cell">
                <label>Expiry Date <span style={{ color: "red" }}>*</span></label>
                <input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-cell">
                <label>Shared Document?</label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={form.is_shared}
                    onChange={(e) => setForm({ ...form, is_shared: e.target.checked })}
                  />
                  Yes (visible to all trucks)
                </label>
              </div>

              <div className="form-cell">
                <label>File Upload {editingDocId ? "(optional)" : ""}</label>
                <input type="file" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                {(form.file_name || form.file_url) && (
                  <div style={{ marginTop: "8px", fontSize: "13px", color: "#374151" }}>
                    Current: {form.file_name || "Existing file"}
                    <button
                      type="button"
                      className="btn-delete"
                      style={{ marginLeft: "10px", fontSize: "12px", padding: "2px 6px" }}
                      onClick={handleDeleteFile}
                    >
                      Remove File
                    </button>
                  </div>
                )}
              </div>

              <div className="form-cell" style={{ gridColumn: "1 / -1" }}>
                <label>Notes (optional)</label>
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any additional information..."
                  style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #d1d5db" }}
                />
              </div>

              <div className="form-actions" style={{ gridColumn: "1 / -1" }}>
                <button type="submit" className="btn-primary">
                  {editingDocId ? "Update Document" : "Add Document"}
                </button>
                {editingDocId && (
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </CollapsibleSection>

          <CollapsibleSection
            title="Documents List"
            id="documentsList"
            isExpanded={expandedSections.documentsList}
            onToggle={() => handleSectionToggle("documentsList")}
            externalControl={true}
          >
            <div className="table-wrap">
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Owner</th>
                    <th>Issue Date</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                    <th>Shared</th>
                    <th>File</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.length === 0 ? (
                    <tr>
                      <td colSpan="8">No documents found.</td>
                    </tr>
                  ) : (
                    filteredDocuments.map((doc) => {
                      const status = getStatus(doc.expiry_date);
                      const displayName = doc.custom_doc_name || doc.doc_type || "-";
                      return (
                        <tr key={doc.id}>
                          <td>{displayName}</td>
                          <td>
                            {doc.owner_type === "truck" ? doc.owner_id : "Company"}
                          </td>
                          <td>{doc.issue_date || "-"}</td>
                          <td>{doc.expiry_date || "-"}</td>
                          <td>
                            <span style={{ color: status.color, fontWeight: "bold" }}>
                              {status.text}
                            </span>
                          </td>
                          <td>{doc.is_shared ? "Yes" : "No"}</td>
                          <td>
                            {doc.file_url ? (
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                View
                              </a>
                            ) : "-"}
                          </td>
                          <td style={{ position: "relative" }}>
                            <button
                              className="menu-button"
                              onClick={() => setActionDocId(doc.id)}
                            >
                              ⋮
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          <FilterModal
            isOpen={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            filters={filters}
            setFilters={setFilters}
            trucks={trucks}
            onApply={() => {}}
            onReset={resetFilters}
          />

          {actionDocId && (
            <ActionModal
              document={documents.find(d => d.id === actionDocId)}
              onClose={() => setActionDocId(null)}
              onEdit={() => openEdit(documents.find(d => d.id === actionDocId))}
              onDelete={() => handleDelete(actionDocId)}
            />
          )}
        </>
      )}
    </div>
  );
}